
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Box,
    CircularProgress,
} from "@mui/material";
import { Edit, Delete, Save, Close, FileCopy, Email } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const MerchantOrderLineDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canEdit, canDelete, canCreate } = usePermission('merchant_order_lines');
    const [line, setLine] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isCloneLoading, setIsCloneLoading] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isResendLoading, setIsResendLoading] = useState(false);

    useEffect(() => {
        fetchLineData();
    }, [id]);

    const fetchLineData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/merchant-order-lines/${id}`);
            setLine(response.data);
        } catch (error) {
            console.error('Error fetching merchant order line data:', error);
            setSnackbar({ open: true, message: 'Error loading merchant order line details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setLine(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/merchant-order-lines/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Merchant Order Line updated successfully', severity: 'success' });
            fetchLineData();
        } catch (error) {
            console.error('Error updating merchant order line:', error);
            setSnackbar({ open: true, message: error.response?.data?.detail || 'Error updating merchant order line', severity: 'error' });
        }
    };

    const handleClone = async () => {
        if (!line) return;
        setIsCloneLoading(true);
        try {
            const response = await axiosInstance.post(`/merchant-order-lines/${id}/clone`);
            setSnackbar({ open: true, message: 'Merchant Order Line cloned successfully', severity: 'success' });
            navigate(`/merchant-order-line/${response.data.id}`);
        } catch (error) {
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error cloning merchant order line',
                severity: 'error',
            });
        } finally {
            setIsCloneLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        setIsDeleteLoading(true);
        try {
            await axiosInstance.delete(`/merchant-order-lines/${id}`);
            setSnackbar({ open: true, message: 'Merchant Order Line deleted successfully', severity: 'success' });
            setTimeout(() => navigate('/merchant-order-lines'), 800);
        } catch (error) {
            console.error('Error deleting merchant order line:', error);
            setSnackbar({ open: true, message: 'Error deleting merchant order line', severity: 'error' });
            throw error;
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const handleResendRequestEmail = async () => {
        setIsResendLoading(true);
        try {
            const response = await axiosInstance.post(`/merchant-order-lines/${id}/resend-request-email`);
            setSnackbar({
                open: true,
                message: response.data?.message || 'Service request email resent',
                severity: response.data?.success === false ? 'error' : 'success',
            });
            fetchLineData();
        } catch (error) {
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error resending service request email',
                severity: 'error',
            });
        } finally {
            setIsResendLoading(false);
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user info for ${userName || 'this user'}.`,
                severity: 'info',
            });
        }
    };

    const formatUnitPrice = (amount, currency) => {
        if (amount == null || amount === '') return '-';
        const value = parseFloat(amount);
        if (Number.isNaN(value)) return '-';
        const cur = currency ? `${currency} ` : '';
        return `${cur}${value.toFixed(2)}`;
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        {['created_by_name', 'updated_by_name'].includes(field.field) ? (
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{field.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() =>
                                        handleUserClick(
                                            field.field === 'created_by_name' ? line.created_by : line.updated_by,
                                            line[field.field]
                                        )
                                    }
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {line[field.field] || '-'}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <InlineEditField
                                label={field.label}
                                value={line?.[field.field]}
                                options={field.options || null}
                                onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                                isEditing={isEditing && !field.readOnly}
                                readOnly={field.readOnly || false}
                                renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!line) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Merchant Order Line not found</MDTypography>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {line.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Merchant Order Line Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox>
                                    {!isEditing ? (
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            {canEdit && line.status !== 'Cancelled' && (
                                                <Tooltip title="Resend service request email">
                                                    <IconButton
                                                        onClick={handleResendRequestEmail}
                                                        sx={commonIconButtonStyles}
                                                        disabled={isResendLoading}
                                                    >
                                                        {isResendLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <Email sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canEdit && (
                                                <Tooltip title="Edit Merchant Order Line">
                                                    <IconButton
                                                        onClick={() => setIsEditing(true)}
                                                        sx={commonIconButtonStyles}
                                                    >
                                                        <Edit sx={commonIconStyles} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canCreate && (
                                                <Tooltip title="Clone Merchant Order Line">
                                                    <IconButton onClick={handleClone} sx={commonIconButtonStyles}>
                                                        {isCloneLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <FileCopy sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete Merchant Order Line">
                                                    <IconButton
                                                        onClick={() => setIsDeleteModalOpen(true)}
                                                        sx={commonIconButtonStyles}
                                                    >
                                                        {isDeleteLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <Delete sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchLineData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}>
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Line Information", [
                                    { label: "Line Name", field: "name" },
                                    {
                                        label: "Order / Booking",
                                        field: "travel_order_id",
                                        readOnly: true,
                                        renderValue: (val) => (
                                            <MDTypography
                                                variant="button"
                                                color={line.travel_order_id ? "info" : "text"}
                                                sx={{ cursor: line.travel_order_id ? 'pointer' : 'default' }}
                                                onClick={() => {
                                                    if (line.travel_order_id) navigate(`/travel-order/${line.travel_order_id}`);
                                                }}
                                            >
                                                {line.travel_order?.name || val || '-'}
                                            </MDTypography>
                                        ),
                                    },
                                    {
                                        label: "Trip Logistics",
                                        field: "package_component_id",
                                        readOnly: true,
                                        renderValue: (val) => {
                                            const label =
                                                line.package_component?.name ||
                                                line.trip_component?.name ||
                                                val ||
                                                '-';
                                            const path = line.package_component_id
                                                ? `/trip-package-components/${line.package_component_id}`
                                                : line.trip_component_id
                                                    ? `/trip-components/${line.trip_component_id}`
                                                    : null;
                                            return (
                                                <MDTypography
                                                    variant="button"
                                                    color={path ? "info" : "text"}
                                                    sx={{ cursor: path ? 'pointer' : 'default' }}
                                                    onClick={() => {
                                                        if (path) navigate(path);
                                                    }}
                                                >
                                                    {label}
                                                </MDTypography>
                                            );
                                        },
                                    },
                                    {
                                        label: "Merchant Allocation",
                                        field: "merchant_allocation_id",
                                        readOnly: true,
                                        renderValue: (val) => (
                                            <MDTypography
                                                variant="button"
                                                color={line.merchant_allocation_id ? "info" : "text"}
                                                sx={{ cursor: line.merchant_allocation_id ? 'pointer' : 'default' }}
                                                onClick={() => {
                                                    if (line.merchant_allocation_id) {
                                                        navigate(`/merchant-allocations/${line.merchant_allocation_id}`);
                                                    }
                                                }}
                                            >
                                                {line.merchant_allocation?.name || val || '-'}
                                            </MDTypography>
                                        ),
                                    },
                                    {
                                        label: "Merchant",
                                        field: "merchant_id",
                                        readOnly: true,
                                        renderValue: () => (
                                            <MDTypography
                                                variant="button"
                                                color={line.merchant_id ? "info" : "text"}
                                                sx={{ cursor: line.merchant_id ? 'pointer' : 'default' }}
                                                onClick={() => {
                                                    if (line.merchant_id) navigate(`/accounts/${line.merchant_id}`);
                                                }}
                                            >
                                                {line.merchant?.name || line.merchant_id || '-'}
                                            </MDTypography>
                                        ),
                                    },
                                    { label: "Pax Count", field: "pax_count" },
                                    {
                                        label: "Unit Price",
                                        field: "unit_price",
                                        renderValue: (val) => formatUnitPrice(val, line.currency),
                                    },
                                ])}

                                {renderSection("Service Information", [
                                    {
                                        label: "Service Start",
                                        field: "service_start",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                    {
                                        label: "Service End",
                                        field: "service_end",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                    {
                                        label: "Status",
                                        field: "status",
                                        options: [
                                            { value: "Waiting For Merchant Acknowledgment", label: "Waiting For Merchant Acknowledgment" },
                                            { value: "Booked", label: "Booked" },
                                            { value: "Cancelled", label: "Cancelled" },
                                        ],
                                    },
                                    { label: "Merchant Confirmation No", field: "merchant_confirmation_no" },
                                    {
                                        label: "Cancellation Date",
                                        field: "cancellation_date",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                ])}

                                {renderSection("Service Request Email", [
                                    {
                                        label: "Request Email Status",
                                        field: "request_email_status",
                                        readOnly: true,
                                        renderValue: (val) => val || '-',
                                    },
                                    {
                                        label: "Request Email Sent At",
                                        field: "request_email_sent_at",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                    {
                                        label: "Request Expires At",
                                        field: "request_expires_at",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                    {
                                        label: "Request Email Error",
                                        field: "request_email_error",
                                        readOnly: true,
                                        renderValue: (val) => val || '-',
                                    },
                                    {
                                        label: "Final Confirmation Status",
                                        field: "final_confirmation_status",
                                        readOnly: true,
                                        renderValue: (val) => val || '-',
                                    },
                                    {
                                        label: "Final Confirmation Sent At",
                                        field: "final_confirmation_sent_at",
                                        readOnly: true,
                                        renderValue: (val) => val ? formatDate(val, 'datetime') : '-',
                                    },
                                ])}

                                {renderSection("Merchant Notes", [
                                    { label: "Merchant Notes", field: "merchant_notes" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    {
                                        label: "Created Date",
                                        field: "created_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime'),
                                    },
                                    {
                                        label: "Modified Date",
                                        field: "updated_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime'),
                                    },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <ConfirmDeleteModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Merchant Order Line"
                message={`Delete ${line.name}? This cannot be undone.`}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantOrderLineDetail;
